import { action, observable } from "mobx";
import * as React from "react";
import { Helper } from "../../../Helper";
import { IContentOrganizerCreate } from "../../../interfaces/IContentOrganizerCreate";
import { IFilterDataContentOrganizer } from "../../../interfaces/IFilterDataContentOrganizer";
import { GroupList, GroupListColumnType, GroupListGroup, GroupListItem } from "../../../interfaces/IGroupList";
import { IHttpStatus } from "../../../interfaces/IHttpStatus";
import { IInstance } from "../../../interfaces/IInstance";
import { ERevisionDateStatus } from "../../../interfaces/IRevisionDate";
import { ContentType, ISite } from "../../../interfaces/ISite";
import { ITreeViewItem } from "../../../interfaces/ITreeViewItem";
import { IUser } from "../../../interfaces/IUser";
import DateTimeService from "../../../services/DateTimeService";
import { LocalizationService } from "../../../services/LocalizationService";
import { IAIcon } from "../../icon/Icon";
import { IATooltip } from "../../tooltip/Tooltip";

export class ContentOrganizerGovernanceStore {
  // Component
  @observable public id: string = Helper.getRandomStringKey();
  @observable public environment: "Development" | "Test" | "Production";
  @observable public token: string;
  @observable public tenant: string;
  @observable public highlightColor: string = "#23879b"; // #1c899d //23879b
  @observable public hasDarkBackground: boolean;
  @observable public width: number;
  @observable public isMobile: boolean;
  @observable public greaterTitleWidth: number = 0;
  // Items
  @observable public siteList: ISite[];
  @observable public siteListCount: number;
  @observable public groupedList: GroupList;
  @observable public groupListItem: GroupListItem[];
  @observable public groups: GroupListGroup[];
  // Search
  @observable public isSearching: boolean;
  @observable public loading: boolean;
  @observable public preview: boolean;
  @observable public searchText = '';
  @observable public isFetchingPages: boolean;
  @observable public showFilter: boolean = true;
  @observable public showSearch: boolean = true;
  // Error 
  @observable public showServerAlert: boolean;
  @observable public errorMessage: IHttpStatus;
  // Paging
  @observable public pageNumber: number = 1;
  @observable public pageSize: number = 20;
  // Filtering
  @observable public hasChanged: string;
  @observable public availableInstances: any[];
  @observable public readableMessageInstances: IInstance[];
  @observable public readableEventsInstances: IInstance[];
  @observable public readableContentOrganizerInstances: IInstance[];
  @observable public showFilterPanel: boolean;
  @observable public showOuterPanel: boolean;
  @observable public filterData: IFilterDataContentOrganizer = {
    isFiltered: false,
    hasChanged: false,
    showPeriod: "All time",
    selectedRevisionDateStatus: [],
    selectedContentOrganizerInstance: [],
    startDate: undefined,
    endDate: undefined,
    selectedContentResponsible: []
  };
  // Categories
  @observable public availableCategoriesForComponent: ITreeViewItem[];
  @observable public availableContentResponsibleForFiltering: ITreeViewItem[] = [];
  @observable public availableContentOrganizerInstancesForFiltering: ITreeViewItem[] = [];
  // Panel
  @observable public panelIsOpen: boolean;
  @observable public isAdminPage: boolean;

  @observable public categoryDataItems: any[];

  @action public updateFilter(filter: IFilterDataContentOrganizer): void {
    this.filterData = filter;
  }

  @action public transformDataToContentOrganizerFilterOptions = (instances: IContentOrganizerCreate[]) => {
    const arrContentOrganizer: string[] = [];
    this.availableContentOrganizerInstancesForFiltering = [];

    for (let i = 0; i < instances.length; i++) {
      const instanceName = instances[i].siteCollection;
      if (!arrContentOrganizer.find(x => !x.toLocaleLowerCase().localeCompare(instanceName.toLocaleLowerCase()))) {
        arrContentOrganizer.push(instanceName);
      }
    }

    arrContentOrganizer.forEach(item => {
      const filterOption: ITreeViewItem = {
        id: item,
        title: item,
        checked: false,
        children: undefined,
        name: item,
        guid: item
      }

      this.availableContentOrganizerInstancesForFiltering.push(filterOption);
    })
  }

  @action public transformDataToContentResponsibleFilterOptions = (sites: ISite[]) => {
    let arrContentResponsible: IUser[] = [];
    this.availableContentResponsibleForFiltering = [];

    for (let i = 0; i < sites.length; i++) {
      const site = sites[i];
      if (site.contentResponsible?.length > 0) {
        const contentResponsible = site.contentResponsible;
        for (let i = 0; i < contentResponsible.length; i++) {
          const user = contentResponsible[i];
          if (!arrContentResponsible.find(x => !x.userPrincipalName.toLocaleLowerCase().localeCompare(user.userPrincipalName.toLocaleLowerCase()))) {
            arrContentResponsible.push(user);
          }
        }
      }
    }

    arrContentResponsible.forEach(item => {
      const filterOption: ITreeViewItem = {
        id: item.userPrincipalName,
        title: item.displayName,
        checked: false,
        children: undefined,
        name: item.displayName,
        guid: item.userPrincipalName
      }

      this.availableContentResponsibleForFiltering.push(filterOption);
    });
  }

  @action public transformDataToGroupList = (sites: ISite[]) => {
    let dateTimeService: DateTimeService = new DateTimeService();
    let items: GroupListItem[] = [];

    this.greaterTitleWidth = 0;
    this.siteList = sites;

    this.siteList.forEach(x => {
      const item = {
        title: x.title,
        id: x.path,
        name: x.title,
        columns: []
      };
      const itemWidthInPixel = 8 * (x.title.length) + 18;

      if (itemWidthInPixel > this.greaterTitleWidth) {
        this.greaterTitleWidth = itemWidthInPixel;
      }

      item.columns.push({
        field: "Title",
        title: new LocalizationService().strings.ContentOrganizerGovernance_Title,
        type: GroupListColumnType.Text,
        value: (
          <div style={{ position: 'relative', top: '5px' }}>
            <IAIcon
              style={{ float: "left", marginRight: 5, marginTop: 1 }}
              url={x.contentType == ContentType.Page ? "https://intraactivestorage.blob.core.windows.net/cdn/icons/Preview%20Link/SVG/ic_fluent_preview_link_24_regular.svg" : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Document/SVG/ic_fluent_document_48_regular.svg"}
              color={this.highlightColor}
              size={18}
            />
            <div style={{ float: 'left', position: 'relative' }}>{x.title}</div>
          </div>
        ),
        width: itemWidthInPixel
      });

      item.columns.push({
        field: "Edit",
        title: "",
        type: GroupListColumnType.Text,
        value: (
          <div style={{ cursor: "pointer" }}>
            <IAIcon
              title={"Edit"}
              color={this.highlightColor}
              size={17}
              onClick={() => {
                window.open(x.linkingUrl ?? x.path, "_blank");
              }}
            />
          </div>
        )
      });

      item.columns.push({
        field: "ContentOrganizer",
        title: new LocalizationService().strings.ContentOrganizerGovernance_ContentOrganizer,
        type: GroupListColumnType.Text,
        value: (
          <div style={{ position: 'relative', top: '3px' }}>
            <div style={{ float: 'left', position: 'relative' }}>{Helper.getSiteCollectionFromPath(x.path)}</div>
          </div>
        ),
        width: itemWidthInPixel
      });

      let contentResponsible = x.contentResponsible;
      const additionalContentResponsible: JSX.Element = (
        <span style={{ fontSize: "small" }}>{contentResponsible?.length > 1 ? ` +${contentResponsible?.length - 1}` : ""}</span>
      );

      item.columns.push({
        field: "ContentResponsible",
        title: new LocalizationService().strings.ContentOrganizerGovernance_ContentResponsible,
        type: GroupListColumnType.Text,
        value: (
          <div style={{ position: 'relative', top: '3px' }}>
            <div style={{ float: 'left', position: 'relative' }}>{contentResponsible?.length > 0  ? contentResponsible[0].displayName : undefined}
              {additionalContentResponsible}
            </div>
          </div>
        )
      });

      let revisionDate = x.revisionDate;
      let statusColor;
      switch (revisionDate.status) {
        case ERevisionDateStatus.Overdue:
          statusColor = "red";
          break;
        case ERevisionDateStatus.Close:
          statusColor = "orange";
          break;
        case ERevisionDateStatus.Clear:
          statusColor = "green";
          break;
      }

      const revisionDateContent: JSX.Element = (x.revisionDate.status === ERevisionDateStatus.Unset)
        ?
        <div>
          <span 
          style={{
            marginRight: 2
          }}>
            {new LocalizationService().strings.ContentOrganizerGovernance_NoContent} 
          </span>
          <div style={{
            float: "right"
          }}>

            <IATooltip
              content="Tooltip"
              type={this.isMobile ? "popup" : "bubble"}
              styles={{
                width: "auto",
                textAlign: "left",
                color: "#333333",
              }}
              componentContent={
                <div>
                  {new LocalizationService().strings.ContentOrganizerGovernance_NoContentTooltip}
              </div>
              }
            >
              <IAIcon
                title={"Info"}
                size={18}
                color={this.highlightColor}
              />
            </IATooltip>
          </div>
        </div>

        :
        <span>{dateTimeService.ConvertToDDMMYYYY(revisionDate.dueDate)}</span>;

      item.columns.push({
        field: "RevisionDate",
        title: new LocalizationService().strings.ContentOrganizerGovernance_RevisionDate,
        type: GroupListColumnType.Text,
        value: (
          <div style={x.revisionDate.status === ERevisionDateStatus.Unset ? { position: 'relative', top: '-17px' } : { position: 'relative', top: '3px' }}>
            <div style={x.revisionDate.status === ERevisionDateStatus.Unset ? { float: 'left', position: 'relative', height: 22 } : { float: 'left', position: 'relative' }}>
              <span style={{
                backgroundColor: statusColor,
                borderRadius: "15px",
                padding: "5px 10px",
                color: x.revisionDate.status === ERevisionDateStatus.Unset ? "#333" : "white"
              }}>
                {revisionDateContent}
              </span>
            </div>
          </div>
        )
      });

      let modified = x.modified;
      item.columns.push({
        field: "Modified",
        title: new LocalizationService().strings.ContentOrganizerGovernance_LastModified,
        type: GroupListColumnType.Text,
        value: dateTimeService.ConvertToDDMMYYYYHHMM(modified, true)
      });

      let modifiedBy = x.modifiedBy;
      item.columns.push({
        field: "ModifiedBy",
        title: new LocalizationService().strings.ContentOrganizerGovernance_LastModifiedBy,
        type: GroupListColumnType.Text,
        value: modifiedBy?.displayName
      });
      items.push(item);
    });

    this.groupListItem = items;
    this.getGroups();
  }

  @action public getGroups(): void {
    const items = this.groupListItem;
    const columnsTitle = [
      // { title: new LocalizationService().strings.ContentOrganizerGovernance_Title, name: "Title", width: this.greaterTitleWidth && this.greaterTitleWidth > 0 ? this.greaterTitleWidth.toString() + "px" : null }, { title: "", name: "Edit" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_Title, name: "Title", width: "20%" }, { title: "", name: "Edit", width: "0px" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_ContentOrganizer, name: "ContentOrganizer" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_ContentResponsible, name: "ContentResponsible" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_RevisionDate, name: "RevisionDate" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_LastModified, name: "LastModified" },
      { title: new LocalizationService().strings.ContentOrganizerGovernance_LastModifiedBy, name: "LastModifiedBy" }
    ];

    this.groups = [{
      name: "group1",
      expanded: true,
      title: "",
      items,
      hideTitle: true,
      borderEveryItem: true
    }];

    this.groupedList = {
      columns: columnsTitle,
      groups: this.groups,
      hideFirstColumn: true
    };
  }
}